import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, map, switchMap, take } from 'rxjs';

import { Offer } from './offers.types';

@Injectable({
  providedIn: 'root',
})
export class OffersService {
  private _offers: BehaviorSubject<Offer[] | null>;
  private _offer: BehaviorSubject<Offer | null>;
  private _projectId: string;

  constructor(private _httpClient: HttpClient) {
    this._offers = new BehaviorSubject(null);
    this._offer = new BehaviorSubject(null);
  }

  get offers$(): Observable<Offer[]> {
    return this._offers.asObservable();
  }

  get offer$(): Observable<Offer> {
    return this._offer.asObservable();
  }

  set projectId(val: string) {
    this._projectId = val;
  }

  get projectId(): string {
    return this._projectId;
  }

  getOffers(params?: HttpParams): Observable<Offer[]> {
    return this._httpClient.get<{ offers: Offer[] }>(`projects/${this.projectId}/offers`, { params }).pipe(
      map(({ offers }) => {
        this._offers.next(offers);
        return offers;
      }),
    );
  }

  getOfferById(id: number): Observable<Offer> {
    return this._httpClient.get<{ offer: Offer }>(`projects/${this.projectId}/offers/${id}`).pipe(
      map(({ offer }) => {
        this._offer.next(offer);
        return offer;
      }),
    );
  }

  createOffer(offer: Offer): Observable<Offer> {
    return this.offers$.pipe(
      take(1),
      switchMap((offers) =>
        this._httpClient.post<{ offer: Offer }>(`projects/${this.projectId}/offers`, { offer: offer }).pipe(
          map(({ offer }) => {
            this._offers.next([offer, ...offers]);

            return offer;
          }),
        ),
      ),
    );
  }

  updateOffer(offer: Offer): Observable<Offer> {
    return this.offers$.pipe(
      take(1),
      switchMap((offers) =>
        this._httpClient.put<{ offer: Offer }>(`projects/${this.projectId}/offers/${offer.id}`, { offer }).pipe(
          map(({ offer }) => {
            const index = offers.findIndex((item) => item.id === offer.id);

            offers[index] = offer;

            this._offers.next(offers);

            return offer;
          }),
        ),
      ),
    );
  }

  deleteOffer(offerId: number): Observable<any> {
    return this.offers$.pipe(
      take(1),
      switchMap((offers) =>
        this._httpClient.delete<{ offer: Offer }>(`projects/${this.projectId}/offers/${offerId}`).pipe(
          map(({ offer }) => {
            const index = offers.findIndex((item) => item.id === offerId);

            offers.splice(index, 1);

            this._offers.next(offers);

            return offer;
          }),
        ),
      ),
    );
  }

  uploadDocument(offer: Offer, document: any): Observable<Offer> {
    const uploadData = new FormData();
    uploadData.append('document[attachment]', document);

    return this.offers$.pipe(
      take(1),
      switchMap((offers) =>
        this._httpClient
          .post<{ offer: Offer }>(`projects/${this.projectId}/offers/${offer.id}/upload_document`, uploadData)
          .pipe(
            map(({ offer }) => {
              const index = offers.findIndex((item) => item.id === offer.id);

              offers[index] = offer;

              this._offers.next(offers);

              this._offer.next(offer);

              return offer;
            }),
          ),
      ),
    );
  }
}
